import React, { useState, useEffect, useRef } from 'react';
import { API_URL } from '../../utils/api';

const OptimizedImage = ({ src, fallbackSrc, alt, className = '', priority = false, sizes = '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw', onClick, ...props }) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [isInView, setIsInView] = useState(priority);
    const [hasError, setHasError] = useState(false);
    const [currentSrc, setCurrentSrc] = useState(src);
    const imgRef = useRef(null);
    const containerRef = useRef(null);

    useEffect(() => {
        if (priority || isInView) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        setIsInView(true);
                        observer.disconnect();
                    }
                });
            },
            { rootMargin: '200px' }
        );

        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => {
            if (containerRef.current) {
                observer.unobserve(containerRef.current);
            }
            observer.disconnect();
        };
    }, [priority, isInView]);

    useEffect(() => {
        if (priority && src) {
            const link = document.createElement('link');
            link.rel = 'preload';
            link.as = 'image';
            link.href = src;
            document.head.appendChild(link);
            return () => {
                document.head.removeChild(link);
            };
        }
    }, [priority, src]);

    // Reset state ONLY when src or fallbackSrc actually changes
    const prevSrcRef = useRef();
    useEffect(() => {
        const initialSrc = src || fallbackSrc;
        
        // Only reset if the source has actually changed
        if (initialSrc !== prevSrcRef.current) {
            if (!initialSrc) {
                setHasError(true);
                setIsLoaded(true);
            } else {
                setCurrentSrc(initialSrc);
                setIsLoaded(false);
                setHasError(false);
            }
            prevSrcRef.current = initialSrc;
        }
    }, [src, fallbackSrc]);

    const handleLoad = () => {
        setIsLoaded(true);
    };

    const handleError = () => {
        if (fallbackSrc && currentSrc !== fallbackSrc) {
            // If the primary image fails, try the fallback!
            setCurrentSrc(fallbackSrc);
        } else {
            setHasError(true);
            setIsLoaded(true);
        }
    };

    const fullSrc = (currentSrc?.startsWith('http') || currentSrc?.startsWith('data:')) ? currentSrc : (currentSrc ? `${API_URL}${currentSrc}` : '');

    return (
        <div ref={containerRef} className={`relative ${className}`}>
            {!hasError && (
                <>
                    {!isLoaded && (
                        <div className="absolute inset-0 bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                            <div className="w-12 h-12 text-gray-400 dark:text-gray-600">
                                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                            </div>
                        </div>
                    )}

                    {isInView && (
                        <img
                            ref={imgRef}
                            src={fullSrc}
                            alt={alt}
                            className={`${className} ${isLoaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-150`}
                            loading={priority ? 'eager' : 'lazy'}
                            decoding="async"
                            sizes={sizes}
                            onLoad={handleLoad}
                            onError={handleError}
                            onClick={onClick}
                            {...props}
                        />
                    )}
                </>
            )}

            {hasError && (
                <div className="absolute inset-0 bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                    <span className="text-4xl text-gray-400 dark:text-gray-600">📷</span>
                </div>
            )}
        </div>
    );
};

export default OptimizedImage;
